// src/encounter/intelligence/run-card-state.ts
//
// What an agent run becomes on a rail card: one of the catalog's CardState values (scrips-signal-ds feat/signal,
// IntelligenceRail.tsx). Pure, so the rail and its tests read one rule. The card draws the catalog's fixed sentence
// for each state (rail-data.ts T); the engine's own words are matched here only to pick the state, never shown.
//
// The engine buffers a run and sends every event at the end (scripsServer.ts EN7), so a finished run ends in either
// `done` (the answer passed its checks) or one `error`:
//   withheld   'This answer did not pass its checks and is withheld. See the chart.'  (runDelivery RUN_WITHHELD_MESSAGE)
//   run-failed status 'failed' (X5: the AI check could not run; Retry)
//   timeout    'timed out after …'
//   denied     not enabled for this patient on this environment (the server-side allowlist, checked again at delivery)
import type { IntelligenceRunView } from '../intelligence-engine';

export type RunCardState = 'idle' | 'loading' | 'answered' | 'withheld' | 'run-failed' | 'timeout' | 'busy' | 'denied' | 'could-not' | 'down' | 'session-expired';

const WITHHELD = /did not pass its checks and is withheld/i;
const NOT_ENABLED = /not enabled for this patient/i;
const TIMED_OUT = /^timed out after/i;

export function cardStateOf(view: Pick<IntelligenceRunView, 'status' | 'events' | 'answer' | 'error' | 'failureStatus'>): RunCardState {
  if (view.status === 'idle') return 'idle';
  if (view.status === 'running') return 'loading';
  if (view.failureStatus != null) {
    if (view.failureStatus === 401) return 'session-expired';
    if (view.failureStatus === 403) return 'denied';
    if (view.failureStatus === 429) return 'busy';
    return 'down';
  }
  const last = [...view.events].reverse().find((e) => e.type === 'error' || e.type === 'done');
  if (last?.type === 'error') {
    if (last.status === 'failed') return 'run-failed';
    const m = typeof last.message === 'string' ? last.message : '';
    if (WITHHELD.test(m)) return 'withheld';
    if (NOT_ENABLED.test(m)) return 'denied';
    if (TIMED_OUT.test(m)) return 'timeout';
    return 'could-not';
  }
  if (view.status === 'error') return 'down';
  return view.answer ? 'answered' : 'could-not';
}

/** X5: the record's critical rows that a failed run still shows (from the rule path, never the model). */
export function criticalRowsOf(view: Pick<IntelligenceRunView, 'events'>): Array<{ text: string; cites: string[] }> {
  const e = [...view.events].reverse().find((x) => x.type === 'error' && x.status === 'failed');
  const rows = (e?.critical as { rows?: unknown } | undefined)?.rows;
  return Array.isArray(rows) ? rows.filter((r): r is { text: string; cites: string[] } => !!r && typeof (r as { text?: unknown }).text === 'string' && Array.isArray((r as { cites?: unknown }).cites)) : [];
}

/** One line of "How this was produced" for a run: what the agent did, in order, and what each check said. */
export type TraceStep =
  | { kind: 'agent'; agent: string; model: string }
  | { kind: 'model-turn'; turn: number }
  | { kind: 'tool'; name: string; count: number | null }
  | { kind: 'check'; check: 'rules' | 'fast' | 'judge'; verdict: 'pass' | 'fail' | 'uncertain' };

/**
 * The trace of a delivered run, from its events: the agent and model, each model turn, each tool the agent called with
 * how many record items it returned, then the rule check, the fast model check and the AI check against the clinical
 * house rules. Names and counts only: tool arguments and results can carry record text and are never read here.
 * A tool call with no result yet (or none returned) has count null.
 */
export function traceOf(view: Pick<IntelligenceRunView, 'events'>): TraceStep[] {
  const steps: TraceStep[] = [];
  const open: Array<Extract<TraceStep, { kind: 'tool' }>> = [];
  const verdict = (v: unknown): 'pass' | 'fail' | 'uncertain' => (v === 'pass' || v === 'fail' ? v : 'uncertain');
  for (const e of view.events) {
    if (e.type === 'start' && typeof e.agent === 'string') steps.push({ kind: 'agent', agent: e.agent, model: typeof e.model === 'string' ? e.model : '' });
    else if (e.type === 'model-turn' && typeof e.turn === 'number') steps.push({ kind: 'model-turn', turn: e.turn });
    else if (e.type === 'tool-call' && typeof e.name === 'string') {
      const s = { kind: 'tool' as const, name: e.name, count: null as number | null };
      steps.push(s);
      open.push(s);
    } else if (e.type === 'tool-result' && typeof e.name === 'string') {
      const i = open.findIndex((s) => s.name === e.name);
      if (i >= 0) open.splice(i, 1)[0].count = typeof e.count === 'number' ? e.count : null;
    } else if (e.type === 'check-rule') steps.push({ kind: 'check', check: 'rules', verdict: e.pass === true ? 'pass' : 'fail' });
    else if (e.type === 'check-fast') steps.push({ kind: 'check', check: 'fast', verdict: verdict(e.verdict) });
    else if (e.type === 'check-judge') steps.push({ kind: 'check', check: 'judge', verdict: verdict(e.verdict) });
  }
  return steps;
}
